import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthLayout from './AuthLayout';
import OtpStep from './OtpStep';
import { Button, PasswordField, PhoneField } from '../../components/common';
import authApi from '../../api/auth/authApi';
import { useToast } from '../../context/ToastContext';
import { PATHS } from '../../routes/paths';
import { DEFAULT_COUNTRY_CODE } from '../../utils/constants';
import * as v from '../../utils/validators';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [step, setStep] = useState('phone');
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [phone, setPhone] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState({});
  const [hint, setHint] = useState('');
  const [loading, setLoading] = useState(false);

  const requestCode = async () => {
    const res = await authApi.requestOtp({ countryCode, phone, purpose: 'reset' });
    setHint(res.hint);
  };

  const sendCode = async (e) => {
    e.preventDefault();
    const err = v.phone(phone);
    setErrors({ phone: err });
    if (err) return;
    setLoading(true);
    try {
      await requestCode();
      setStep('code');
    } catch (ex) {
      setErrors({ phone: ex.message });
    }
    setLoading(false);
  };

  const verify = async (code) => {
    setLoading(true);
    setErrors({});
    try {
      const res = await authApi.verifyOtp({ countryCode, phone, code, purpose: 'reset' });
      setResetToken(res.resetToken);
      setStep('password');
    } catch (ex) {
      setErrors({ code: ex.message });
    }
    setLoading(false);
  };

  const save = async (e) => {
    e.preventDefault();
    const errs = v.validate({ password, confirm }, {
      password: v.password,
      confirm: (c) => (c === password ? '' : 'Passwords don’t match'),
    });
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      await authApi.resetPassword({ resetToken, password });
      toast.show('Password updated. Please log in.');
      navigate(PATHS.login, { replace: true });
    } catch (ex) {
      setErrors({ password: ex.message });
      setLoading(false);
    }
  };

  if (step === 'code') {
    return (
      <AuthLayout title="Enter your code" onBack={() => setStep('phone')}>
        <OtpStep
          countryCode={countryCode}
          phone={phone}
          hint={hint}
          error={errors.code}
          loading={loading}
          onVerify={verify}
          onResend={requestCode}
        />
      </AuthLayout>
    );
  }

  if (step === 'password') {
    return (
      <AuthLayout title="Set a new password" onBack={() => setStep('phone')}>
        <form onSubmit={save} noValidate>
          <PasswordField
            label="New password"
            placeholder="At least 8 characters"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errors.password}
          />
          <PasswordField
            label="Confirm new password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            error={errors.confirm}
          />
          <Button type="submit" block className="u-mt-6" loading={loading}>Update password</Button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Forgot password?" subtitle="Enter your mobile number and we’ll text you a code to reset it.">
      <form onSubmit={sendCode} noValidate>
        <PhoneField
          countryCode={countryCode}
          onCountryCodeChange={setCountryCode}
          value={phone}
          onChange={setPhone}
          error={errors.phone}
        />
        <Button type="submit" block className="u-mt-6" loading={loading}>Send code</Button>
      </form>
    </AuthLayout>
  );
}
